import { useState, useEffect, useCallback } from 'react';
import * as analyticsApi from '../../api/analytics';
import ForecastConfigWizard from './ForecastConfigWizard';
import FeatureImportancePanel from './FeatureImportancePanel';
import SHAPInteractionHeatmap from './SHAPInteractionHeatmap';
import ModelVersionComparison from './ModelVersionComparison';
import QualityEDAReport from './QualityEDAReport';
import CorrelationEDAReport from './CorrelationEDAReport';
import CleansePhase from './CleansePhase';
import ModelSpecPhase from './ModelSpecPhase';

// ============================================================================
// Status badge helpers
// ============================================================================

const STATUS_COLORS = {
  draft:       { background: '#e5e7eb', color: '#374151' },  // gray
  assembling:  { background: '#dbeafe', color: '#1d4ed8' },  // blue
  training:    { background: '#dbeafe', color: '#1d4ed8' },  // blue
  assembled:   { background: '#d1fae5', color: '#065f46' },  // green
  trained:     { background: '#d1fae5', color: '#065f46' },  // green
  failed:      { background: '#fee2e2', color: '#991b1b' },  // red
};

function StatusBadge({ status }) {
  const style = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      style={{
        ...style,
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    >
      {status || 'unknown'}
    </span>
  );
}

// ============================================================================
// QuickEditForm — name + description only; sends PUT without config (preserves Test 16)
// ============================================================================

function QuickEditForm({ config, onSaved, onCancel }) {
  const [name, setName] = useState(config.name);
  const [description, setDescription] = useState(config.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      await analyticsApi.updateForecastConfig(config.id, { name: name.trim(), description: description.trim() });
      onSaved();
    } catch (err) {
      setError(err.data?.message || err.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
      <h3 className="text-base font-semibold text-gray-900 mb-3">Edit Forecast Config</h3>
      {error && <div style={{ color: '#991b1b', background: '#fee2e2', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '0.875rem' }}>{error}</div>}
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">Name <span style={{ color: '#dc2626' }}>*</span></label>
        <input data-testid="forecast-config-name-input" type="text"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Q4 Win-Rate Forecast" required />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <input data-testid="forecast-config-description-input" type="text"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="forecast-config-save-btn"
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// NewConfigForm — wizard-backed create form
// ============================================================================

function NewConfigForm({ onSave, onCancel }) {
  return (
    <ForecastConfigWizard
      title="New Forecast Config"
      submitLabel="Create"
      onSubmit={async (_name, _description, payload) => {
        // payload always has config (configDirty starts true for new)
        const res = await analyticsApi.createForecastConfig(payload);
        onSave(res.data || res);
      }}
      onCancel={onCancel}
    />
  );
}

// ============================================================================
// Feature Runs panel
// ============================================================================

function FeatureRunsPanel({ configId }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.listFeatureRuns(configId);
      const data = res.data ?? res;
      setRuns(Array.isArray(data) ? data : (data?.runs || []));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load runs.');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-gray-500">Loading runs…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (runs.length === 0) return <p className="text-sm text-gray-400 italic">No runs yet.</p>;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
          <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontWeight: 600 }}>Run ID</th>
          <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontWeight: 600 }}>Status</th>
          <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280', fontWeight: 600 }}>Created</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <tr key={run.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: '#374151' }}>
              {run.id?.slice(0, 8) || '—'}
            </td>
            <td style={{ padding: '4px 8px' }}>
              <StatusBadge status={run.status} />
            </td>
            <td style={{ padding: '4px 8px', color: '#6b7280' }}>
              {run.created_at ? new Date(run.created_at).toLocaleString() : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ============================================================================
// EDA Suggestions panel
// ============================================================================

function EDASuggestionsPanel({ configId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.listEDASuggestions(configId);
      const data = res.data ?? res;
      setSuggestions(Array.isArray(data) ? data : (data?.suggestions || []));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load EDA suggestions.');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-sm text-gray-500">Loading suggestions…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (suggestions.length === 0) return <p className="text-sm text-gray-400 italic">No suggestions. Run Quality EDA first.</p>;

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {suggestions.map((s, i) => (
        <li
          key={i}
          style={{
            padding: '6px 10px',
            borderLeft: '3px solid #93c5fd',
            background: '#eff6ff',
            marginBottom: '6px',
            borderRadius: '0 4px 4px 0',
            fontSize: '0.8125rem',
            color: '#1e3a5f',
          }}
        >
          {typeof s === 'string' ? s : s.message || JSON.stringify(s)}
        </li>
      ))}
    </ul>
  );
}

// ============================================================================
// SHAPInteractionHeatmapLoader — fetches features then renders heatmap
// ============================================================================

function SHAPInteractionHeatmapLoader({ configId }) {
  const [interactions, setInteractions] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    analyticsApi.getForecastFeatures(configId)
      .then(res => { setInteractions((res.data ?? res)?.shap_interactions || []); setLoading(false); })
      .catch(() => { setInteractions([]); setLoading(false); });
  }, [configId]);
  if (loading) return <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Loading…</div>;
  return <SHAPInteractionHeatmap interactions={interactions} />;
}

// ============================================================================
// PredictPanel
// ============================================================================

function PredictPanel({ configId }) {
  const [recordId, setRecordId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlePredict = async (e) => {
    e.preventDefault();
    if (!recordId.trim()) return;
    setLoading(true); setResult(null); setError(null);
    try {
      const res = await analyticsApi.predictOne(configId, recordId.trim());
      setResult(res.data || res);
    } catch (err) {
      const code = err.data?.error || err.response?.data?.error;
      if (code === 'no_active_model') setError('No active model — promote a trained run first.');
      else if (code === 'record_not_found') setError('Record not found in prediction table.');
      else setError(err.data?.message || err.response?.data?.message || err.message || 'Prediction failed.');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid={`predict-panel-${configId}`} style={{ marginBottom: '16px' }}>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">Predict</h4>
      <form onSubmit={handlePredict} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          data-testid="predict-record-id-input"
          type="text"
          className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Record ID"
          value={recordId}
          onChange={e => setRecordId(e.target.value)}
          style={{ flex: 1, maxWidth: '300px' }}
        />
        <button
          type="submit"
          disabled={loading || !recordId.trim()}
          className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? 'Scoring…' : 'Score'}
        </button>
      </form>
      {error && <div data-testid="predict-error" style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '6px' }}>{error}</div>}
      {result && (
        <div data-testid="predict-result" style={{ marginTop: '6px', fontSize: '0.875rem', color: '#065f46' }}>
          Score: {(result.score * 100).toFixed(1)}%
          {result.run_id && <span style={{ marginLeft: '8px', color: '#6b7280' }}>run {result.run_id.slice(0, 8)}</span>}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Config Card
// ============================================================================

function ConfigCard({ config, onRefresh, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // 'assemble' | 'train' | 'promote' | 'quality-eda' | 'corr-eda'

  // For train: pick a run first
  const [runs, setRuns] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [showRunPicker, setShowRunPicker] = useState(false);

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Edit state (name/description only) and Configure state (full wizard)
  const [showEdit, setShowEdit] = useState(false);
  const [showConfigure, setShowConfigure] = useState(false);

  // Workbench tab state
  const [workbenchTab, setWorkbenchTab] = useState('cleanse'); // 'cleanse' | 'model-spec'

  const doAction = async (label, fn) => {
    setActionLoading(label);
    setActionError(null);
    try {
      await fn();
      onRefresh();
    } catch (err) {
      setActionError(err.response?.data?.message || err.message || `Failed: ${label}`);
    } finally {
      setActionLoading(null);
    }
  };

  // Load runs when card expands (needed for EDA/workbench sections)
  useEffect(() => {
    if (expanded && runs === null) {
      analyticsApi.listFeatureRuns(config.id)
        .then(res => {
          const data = res.data ?? res;
          setRuns(Array.isArray(data) ? data : (data?.runs || []));
        })
        .catch(() => setRuns([]));
    }
  }, [expanded, config.id]);

  const handleAssemble = () => doAction('assemble', () => analyticsApi.assembleForecastRun(config.id));

  const handleOpenTrainPicker = async () => {
    setShowRunPicker(true);
    if (runs === null) {
      try {
        const res = await analyticsApi.listFeatureRuns(config.id);
        const data = res.data ?? res;
        const runsList = Array.isArray(data) ? data : (data?.runs || []);
        setRuns(runsList);
        const assembled = runsList.find(r => r.status === 'assembled');
        if (assembled) setSelectedRunId(assembled.id);
      } catch {
        setRuns([]);
      }
    }
  };

  const handleTrain = () => {
    if (!selectedRunId) return;
    setShowRunPicker(false);
    doAction('train', () => analyticsApi.trainForecastRun(config.id, selectedRunId));
  };

  const handlePromote = async () => {
    // Promote the latest trained run
    let runId = selectedRunId;
    if (!runId) {
      try {
        const res = await analyticsApi.listFeatureRuns(config.id);
        const trained = (res.data?.runs || res.runs || []).find(r => r.status === 'trained');
        if (trained) runId = trained.id;
      } catch {
        // ignore
      }
    }
    if (!runId) {
      setActionError('No trained run found to promote.');
      return;
    }
    doAction('promote', () => analyticsApi.promoteForecastRun(config.id, runId));
  };

  const handleQualityEDA = () => doAction('quality-eda', () => analyticsApi.runQualityEDA(config.id));

  const handleCorrEDA = () => {
    if (!selectedRunId) {
      setActionError('Select a run first to run Correlation EDA.');
      return;
    }
    doAction('corr-eda', () => analyticsApi.runCorrelationEDA(config.id, selectedRunId));
  };

  const isWorking = !!actionLoading;

  return (
    <div
      data-testid={`forecast-config-card-${config.id}`}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        background: '#fff',
        marginBottom: '12px',
      }}
    >
      {/* Card header */}
      <div
        data-testid={`forecast-config-expand-${config.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: '0.9375rem' }}>
          {config.name}
        </span>
        <StatusBadge status={config.status} />
        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
          {config.created_at ? new Date(config.created_at).toLocaleDateString() : ''}
        </span>
        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          {config.description && (
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '12px' }}>
              {config.description}
            </p>
          )}

          {actionError && (
            <div style={{ color: '#991b1b', background: '#fee2e2', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '0.875rem' }}>
              {actionError}
            </div>
          )}

          {/* Predict panel */}
          <PredictPanel configId={config.id} />

          {/* Edit form (name/description only — does not mutate config) */}
          {showEdit && (
            <QuickEditForm
              config={config}
              onSaved={() => { setShowEdit(false); onRefresh(); }}
              onCancel={() => setShowEdit(false)}
            />
          )}

          {/* Configure wizard (full 6-step config editor) */}
          {showConfigure && (
            <ForecastConfigWizard
              title="Configure Forecast"
              initialName={config.name}
              initialDescription={config.description || ''}
              initialConfig={config.config}
              submitLabel="Save"
              onSubmit={async (_name, _description, payload) => {
                await analyticsApi.updateForecastConfig(config.id, payload);
                setShowConfigure(false);
                onRefresh();
              }}
              onCancel={() => setShowConfigure(false)}
            />
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <button
              onClick={handleAssemble}
              disabled={isWorking}
              className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {actionLoading === 'assemble' ? 'Assembling…' : 'Assemble'}
            </button>

            <button
              onClick={handleOpenTrainPicker}
              disabled={isWorking}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Train…
            </button>

            <button
              onClick={handlePromote}
              disabled={isWorking || config.forecast_type === 'survival'}
              title={config.forecast_type === 'survival' ? 'Survival models produce artifacts only — promotion not supported in Phase 1' : undefined}
              className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading === 'promote' ? 'Promoting…' : 'Promote'}
            </button>

            <button
              onClick={handleQualityEDA}
              disabled={isWorking}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {actionLoading === 'quality-eda' ? 'Running…' : 'Quality EDA'}
            </button>

            <button
              onClick={handleCorrEDA}
              disabled={isWorking}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {actionLoading === 'corr-eda' ? 'Running…' : 'Correlation EDA'}
            </button>

            <button
              data-testid={`forecast-edit-btn-${config.id}`}
              onClick={() => setShowEdit(true)}
              disabled={isWorking || config.status === 'assembling' || config.status === 'training'}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Edit
            </button>

            <button
              data-testid={`forecast-configure-btn-${config.id}`}
              onClick={() => setShowConfigure(true)}
              disabled={isWorking || config.status === 'assembling' || config.status === 'training'}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Configure
            </button>

            {!confirmDelete ? (
              <button
                data-testid={`forecast-delete-btn-${config.id}`}
                onClick={() => setConfirmDelete(true)}
                disabled={isWorking || config.status === 'assembling' || config.status === 'training'}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Delete this config?</span>
                <button
                  data-testid={`forecast-delete-confirm-${config.id}`}
                  onClick={async () => {
                    setDeleteLoading(true);
                    try {
                      await analyticsApi.deleteForecastConfig(config.id);
                      onDeleted(config.id);
                    } catch (err) {
                      setDeleteError(err.response?.data?.message || err.message || 'Delete failed.');
                      setConfirmDelete(false);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }}
                  disabled={deleteLoading}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button
                  onClick={() => { setConfirmDelete(false); setDeleteError(null); }}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            )}
            {deleteError && (
              <div style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '4px' }}>{deleteError}</div>
            )}
          </div>

          {/* Train run picker */}
          {showRunPicker && (
            <div
              style={{
                background: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px',
              }}
            >
              <p className="text-sm font-medium text-gray-700 mb-2">Select a run to train:</p>
              {runs === null ? (
                <p className="text-sm text-gray-500">Loading runs…</p>
              ) : runs.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No runs. Assemble first.</p>
              ) : (
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm mr-2"
                  value={selectedRunId}
                  onChange={(e) => setSelectedRunId(e.target.value)}
                >
                  <option value="">— select run —</option>
                  {runs.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.id?.slice(0, 8)} ({r.status})
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={handleTrain}
                disabled={!selectedRunId || isWorking}
                className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 mr-2"
              >
                {actionLoading === 'train' ? 'Training…' : 'Train'}
              </button>
              <button
                onClick={() => setShowRunPicker(false)}
                className="px-3 py-1 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}

          {/* EDA Suggestions */}
          <div style={{ marginBottom: '16px' }}>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">EDA Suggestions</h4>
            <EDASuggestionsPanel configId={config.id} />
          </div>

          {/* Feature Runs */}
          <div style={{ marginBottom: '16px' }}>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Feature Runs</h4>
            <FeatureRunsPanel configId={config.id} />
          </div>

          {/* EDA */}
          <div style={{ marginBottom: '16px' }}>
            <details open>
              <summary style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>Data Quality</summary>
              <div style={{ marginTop: '8px' }}>
                <QualityEDAReport configId={config.id} />
              </div>
            </details>
          </div>

          {(() => {
            const activeRunWithEDA = runs?.find(r => r.eda_status === 'completed');
            return activeRunWithEDA ? (
              <div style={{ marginBottom: '16px' }}>
                <details open>
                  <summary style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>Correlation EDA</summary>
                  <div style={{ marginTop: '8px' }}>
                    <CorrelationEDAReport configId={config.id} runId={activeRunWithEDA.id} />
                  </div>
                </details>
              </div>
            ) : null;
          })()}

          {(config.status === 'trained' || config.status === 'assembled') && <>
            <div style={{ marginBottom: '16px' }}>
              <details open>
                <summary style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>Feature Importance</summary>
                <div style={{ marginTop: '8px' }}>
                  <FeatureImportancePanel configId={config.id} config={config} />
                </div>
              </details>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <details>
                <summary style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>SHAP Interactions</summary>
                <div style={{ marginTop: '8px' }}>
                  <SHAPInteractionHeatmapLoader configId={config.id} />
                </div>
              </details>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <details>
                <summary style={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151', cursor: 'pointer', userSelect: 'none' }}>Model Versions</summary>
                <div style={{ marginTop: '8px' }}>
                  <ModelVersionComparison configId={config.id} forecastType={config.forecast_type || 'binary'} />
                </div>
              </details>
            </div>
          </>}

          {/* Workbench phases: Cleanse + Model Spec */}
          <div data-testid="workbench-section" style={{ marginTop: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Workbench tab bar */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button
                data-testid="workbench-tab-cleanse"
                onClick={() => setWorkbenchTab('cleanse')}
                style={{
                  padding: '8px 20px',
                  fontSize: '0.875rem',
                  fontWeight: workbenchTab === 'cleanse' ? 600 : 400,
                  color: workbenchTab === 'cleanse' ? '#1d4ed8' : '#6b7280',
                  background: workbenchTab === 'cleanse' ? '#fff' : 'transparent',
                  border: 'none',
                  borderBottom: workbenchTab === 'cleanse' ? '2px solid #1d4ed8' : '2px solid transparent',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                Cleanse
              </button>
              <button
                data-testid="workbench-tab-model-spec"
                onClick={() => setWorkbenchTab('model-spec')}
                style={{
                  padding: '8px 20px',
                  fontSize: '0.875rem',
                  fontWeight: workbenchTab === 'model-spec' ? 600 : 400,
                  color: workbenchTab === 'model-spec' ? '#1d4ed8' : '#6b7280',
                  background: workbenchTab === 'model-spec' ? '#fff' : 'transparent',
                  border: 'none',
                  borderBottom: workbenchTab === 'model-spec' ? '2px solid #1d4ed8' : '2px solid transparent',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                Model Spec
              </button>
            </div>

            {/* Workbench phase content */}
            {workbenchTab === 'cleanse' && (
              <CleansePhase
                configId={config.id}
                config={config.config}
                featureRuns={runs || []}
                onRulesChanged={() => { onRefresh(); }}
              />
            )}
            {workbenchTab === 'model-spec' && (
              <ModelSpecPhase
                configId={config.id}
                config={config.config}
                forecastType={config.forecast_type}
                onSpecSaved={() => { onRefresh(); }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ForecastTab — main component
// ============================================================================

function ForecastTab() {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);

  const loadConfigs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.listForecastConfigs();
      // API returns a raw array (not wrapped in { configs: [...] })
      const data = res.data ?? res;
      setConfigs(Array.isArray(data) ? data : (data?.configs || []));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load forecast configs.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleCreated = (newConfig) => {
    setShowNewForm(false);
    setConfigs((prev) => [newConfig, ...prev]);
  };

  return (
    <div data-testid="forecast-tab-container">
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Forecast Workbench</h2>
          <p className="text-sm text-gray-500 mt-0.5">Build, train, and promote ML forecast models on your CRM data.</p>
        </div>
        <button
          data-testid="new-config-btn"
          onClick={() => setShowNewForm((v) => !v)}
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700"
        >
          {showNewForm ? 'Cancel' : '+ New Config'}
        </button>
      </div>

      {/* New config form */}
      {showNewForm && (
        <NewConfigForm
          onSave={handleCreated}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      {/* Loading / error */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">Loading forecast configurations…</div>
      )}

      {!isLoading && error && (
        <div style={{ color: '#991b1b', background: '#fee2e2', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
          {error}
          <button
            onClick={loadConfigs}
            style={{ marginLeft: '12px', textDecoration: 'underline', color: '#7f1d1d', cursor: 'pointer', background: 'none', border: 'none' }}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && configs.length === 0 && !showNewForm && (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 24px',
            border: '2px dashed #e5e7eb',
            borderRadius: '12px',
            color: '#9ca3af',
          }}
        >
          <p className="text-lg font-medium mb-2">No forecast configs yet</p>
          <p className="text-sm">Create your first config to get started with ML forecasting.</p>
        </div>
      )}

      {/* Configs list */}
      {!isLoading && !error && configs.length > 0 && (
        <div data-testid="forecast-configs-list">
          {configs.map((config) => (
            <ConfigCard
              key={config.id}
              config={config}
              onRefresh={loadConfigs}
              onDeleted={(id) => setConfigs(prev => prev.filter(c => c.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ForecastTab;
